import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { EMAIL_PATTERN } from '@avanta/shared/constants/patterns';
import { useLogin } from '@avanta/shared/hooks/useLogin';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleCheckIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';

export const MagicLink = (): React.ReactElement => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('t');
  const email = params.get('email');
  const { t } = useTranslation(LOCIZE_NS.AUTH);
  const navigate = useNavigate();
  const { trackUserLogin } = useTrackingEvents();
  const { login, isLoading } = useLogin({
    authenticate: AuthService.login,
    onSuccess: authInfo => {
      trackUserLogin(authInfo.user, authInfo.access_token);
      navigate(ROUTES.ROOT);
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<{ email: string }>({
    mode: 'onChange',
    defaultValues: { email: email || '' },
  });
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const isMagicLinkSent = useRef(false);

  const handleOnSubmit = async (data: { email: string }): Promise<void> => {
    try {
      setIsSending(true);
      await AuthService.requestMagicLink(data.email);
      setSubmittedEmail(data.email);
    } catch {
      toast.error(t('MagicLink.error'));
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (token && email && !isLoading && !isMagicLinkSent.current) {
      login(email, '', token);
      isMagicLinkSent.current = true;
    }
  }, [login, isLoading, token, email]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={submittedEmail ? 'success' : 'form'}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-center max-am-lg:mt-32 am-xl:h-screen am-xl:p-24 bg-gray-50"
      >
        <Card className="w-full max-w-[calc(100%-2rem)] am-md:max-w-sm am-md:p-10 p-5 bg-white rounded-2xl shadow-md">
          {submittedEmail ? (
            <div className="flex flex-col justify-center items-center text-sm font-medium text-gray-500 gap-4 text-center">
              <CircleCheckIcon className="size-18 text-teal-600 text-center" />
              <span className="text-lg font-bold text-denim-600">
                {t('MagicLink.success.heading')}
              </span>
              <span className="text-sm font-medium text-gray-500">{submittedEmail}</span>
              <span className="text-sm text-denim-600">{t('MagicLink.success.body')}</span>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2 mb-4">
                <h2 className="text-2xl font-bold text-center text-denim-600">
                  {t('MagicLink.heading')}
                </h2>
                <h4 className="text-sm font-medium text-center text-gray-500 ">
                  {t('MagicLink.body')}
                </h4>
              </div>
              <form onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-6 ">
                <Input
                  type="email"
                  placeholder={t('MagicLink.placeholder')}
                  showPassword={false}
                  autoComplete="email"
                  {...register('email', {
                    required: t('MagicLink.email.required'),
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: 'MagicLink.email.error',
                    },
                  })}
                />
                {errors.email?.message && (
                  <p className="text-red-500 font-medium text-xs">{t(errors.email.message)}</p>
                )}
                <Button type="submit" className="w-full" disabled={!isValid} isLoading={isSending}>
                  {t('MagicLink.cta')}
                </Button>
              </form>
            </div>
          )}
          <Button
            variant="link"
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            className="text-center p-0 h-fit text-sm font-medium text-gray-500 hover:text-gray-700 w-full mt-6"
          >
            {t('MagicLink.goLogin')}
          </Button>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};
